// Página "Citas" — solicitar cita a demanda con el abogado
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  PhoneIcon,
  VideoCameraIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useClienteFichaQuery } from '@/hooks/useClienteExpediente';
import { api } from '@/services/api';

interface CitaSolicitada {
  id: string;
  motivo: string;
  modalidad: string;
  notas?: string;
  estado: 'pendiente' | 'confirmada' | 'completada' | 'cancelada';
  created_at: string;
}

const MODALIDAD_LABELS: Record<string, string> = {
  telefonica: 'Telefónica',
  videollamada: 'Videollamada',
  presencial: 'Presencial',
};

export function ClientesCitas() {
  const { t } = useTranslation('portal');
  const { data: ficha } = useClienteFichaQuery();
  const [motivo, setMotivo] = useState('');
  const [modalidad, setModalidad] = useState('');
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const MOTIVOS_CITA = [
    { value: 'consulta_general', label: t('appointments.reasonGeneral') },
    { value: 'documentacion', label: t('appointments.reasonDocs') },
    { value: 'estado_expediente', label: t('appointments.reasonStatus') },
    { value: 'renovacion', label: t('appointments.reasonRenewal') },
    { value: 'recurso', label: t('appointments.reasonAppeal') },
    { value: 'cambio_situacion', label: t('appointments.reasonChange') },
    { value: 'otro', label: t('appointments.reasonOther') },
  ];

  const MODALIDADES = [
    { value: 'telefonica', label: t('appointments.modalityPhone'), icon: PhoneIcon, description: t('appointments.modalityPhoneDesc') },
    { value: 'videollamada', label: t('appointments.modalityVideo'), icon: VideoCameraIcon, description: t('appointments.modalityVideoDesc') },
    { value: 'presencial', label: t('appointments.modalityInPerson'), icon: BuildingOfficeIcon, description: t('appointments.modalityInPersonDesc') },
  ];

  // Citas del timeline (tipo appointment)
  const citas: CitaSolicitada[] = (ficha?.timeline ?? [])
    .filter((item) => item.type === 'appointment')
    .map((item) => ({
      id: item.id,
      motivo: item.title || 'Cita solicitada',
      modalidad: (item.metadata?.modalidad as string) || 'telefonica',
      notas: item.description || undefined,
      estado: (item.metadata?.estado as CitaSolicitada['estado']) || 'pendiente',
      created_at: item.timestamp,
    }));

  const handleSolicitar = async () => {
    if (!motivo || !modalidad) return;
    setEnviando(true);
    setError(null);
    try {
      await api.post('/clientes/citas', {
        motivo,
        modalidad,
        notas: notas || undefined,
      });
      setEnviado(true);
      setMotivo('');
      setModalidad('');
      setNotas('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('appointments.submitError', 'No se pudo enviar la solicitud. Inténtalo de nuevo.');
      setError(message);
    } finally {
      setEnviando(false);
    }
  };

  const ESTADO_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    pendiente: { label: t('appointments.statusPending'), variant: 'secondary' },
    confirmada: { label: t('appointments.statusConfirmed'), variant: 'default' },
    completada: { label: t('appointments.statusCompleted'), variant: 'outline' },
    cancelada: { label: t('appointments.statusCancelled'), variant: 'destructive' },
  };

  return (
    <ClientLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('appointments.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('appointments.subtitle')}
          </p>
        </div>

        {/* Formulario de solicitud */}
        {enviado ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <CheckCircleIcon className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('appointments.requestSent')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
                {t('appointments.requestSentDesc')}
              </p>
              <Button onClick={() => setEnviado(false)} variant="outline">
                {t('appointments.requestAnother')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <CalendarDaysIcon className="h-5 w-5" aria-hidden="true" />
                {t('appointments.newRequest')}
              </h3>

              {/* Motivo */}
              <div>
                <label htmlFor="motivo-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('appointments.reasonLabel')} <span className="text-red-500">*</span>
                </label>
                <select
                  id="motivo-select"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                >
                  <option value="">{t('appointments.selectReason')}</option>
                  {MOTIVOS_CITA.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Modalidad */}
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('appointments.modalityLabel')} <span className="text-red-500">*</span>
                </legend>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup">
                  {MODALIDADES.map((m) => {
                    const Icon = m.icon;
                    const selected = modalidad === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setModalidad(m.value)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                          selected
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${selected ? 'text-primary' : 'text-gray-400'}`} aria-hidden="true" />
                        <span className={`text-sm font-medium ${selected ? 'text-primary' : 'text-gray-700 dark:text-gray-300'}`}>
                          {m.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{m.description}</span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              {/* Notas */}
              <div>
                <label htmlFor="cita-notas" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('appointments.notesLabel')}
                </label>
                <textarea
                  id="cita-notas"
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder={t('appointments.notesPlaceholder')}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{notas.length}/500</p>
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 flex items-start gap-2" role="alert">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              <Button
                onClick={handleSolicitar}
                disabled={!motivo || !modalidad || enviando}
                className="w-full"
              >
                {enviando ? t('appointments.submitting') : t('appointments.submitButton')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Historial de citas */}
        <Card>
          <CardContent className="pt-6 space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <ClockIcon className="h-5 w-5" aria-hidden="true" />
              {t('appointments.history')}
            </h3>
            {citas.length > 0 ? (
              citas.map((cita) => {
                const estadoInfo = ESTADO_BADGE[cita.estado] || { label: cita.estado, variant: 'secondary' as const };
                return (
                  <div
                    key={cita.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {cita.motivo}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {new Date(cita.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                        {' · '}
                        {MODALIDAD_LABELS[cita.modalidad] || cita.modalidad}
                      </p>
                    </div>
                    <Badge variant={estadoInfo.variant}>{estadoInfo.label}</Badge>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                {t('appointments.noHistory', 'Aún no tienes citas solicitadas.')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
}
